// onboarding-swipe — lógica PURA do gesto de swipe do onboarding mobile (parede A2).
//
// Separada da UI de propósito: o ambiente de teste é `node` (sem DOM), então a
// regra de "arrastou o suficiente pra trocar de slide" precisa ser testável sem
// simular PointerEvents reais. O componente MobileOnboarding chama estas funções
// a partir dos handlers de pointerdown/move/up; aqui não há React nem DOM.

/** Fração da largura do slide que o dedo precisa percorrer pra "comprometer" a
 *  troca de slide ao soltar (sem flick). Espelha carrosséis nativos (~30%). */
export const SWIPE_DISTANCE_RATIO = 0.3

/** Velocidade mínima (px/ms) pra um "flick" trocar de slide mesmo com pouca
 *  distância. Gesto rápido e curto também avança/volta, como no iOS/Android. */
export const SWIPE_FLICK_VELOCITY = 0.5

/** Mantém um índice dentro de [0, count-1]. count<=0 → 0. */
export function clampIndex(index: number, count: number): number {
  if (count <= 0) return 0
  if (index < 0) return 0
  if (index > count - 1) return count - 1
  return index
}

export interface ResolveSwipeArgs {
  /** Índice do slide atual (antes de soltar). */
  current: number
  /** Total de slides. */
  count: number
  /** Deslocamento horizontal do gesto (px). Negativo = arrastou pra ESQUERDA
   *  (quer AVANÇAR pro próximo); positivo = pra DIREITA (quer VOLTAR). */
  deltaX: number
  /** Largura de referência do slide/viewport (px) pro cálculo do threshold. */
  width: number
  /** Duração do gesto (ms), pra detectar flick. <=0 desliga o atalho de flick. */
  durationMs: number
}

/**
 * Decide o índice do slide DEPOIS de soltar o dedo. Troca de slide se o arrasto
 * passou do threshold de distância (SWIPE_DISTANCE_RATIO da largura) OU foi um
 * flick rápido (velocidade acima de SWIPE_FLICK_VELOCITY). Caso contrário,
 * encaixa de volta no slide atual (snap). Nunca passa das bordas (rubber-band é
 * só visual; o índice fica preso em [0, count-1]).
 */
export function resolveSwipe({ current, count, deltaX, width, durationMs }: ResolveSwipeArgs): number {
  if (count <= 1 || width <= 0) return clampIndex(current, count)

  const distance = Math.abs(deltaX)
  const velocity = durationMs > 0 ? distance / durationMs : 0
  const passedDistance = distance >= width * SWIPE_DISTANCE_RATIO
  const passedFlick = velocity >= SWIPE_FLICK_VELOCITY && distance > 0

  if (!passedDistance && !passedFlick) return clampIndex(current, count)

  // deltaX < 0 (arrastou pra esquerda) → próximo slide; > 0 → slide anterior.
  const direction = deltaX < 0 ? 1 : -1
  return clampIndex(current + direction, count)
}
